import { boolean, object, string } from "yup";

import { emailOptional, nonEmptyString, unit } from "@covertree/utils";
import { OptionType } from "@covertree/types";

import type { Lender } from "@covertree/features";

import { ConsentsFormState } from "../../types";

export interface MortgageBillFormState extends ConsentsFormState {
  lender: Omit<Lender, "name" | "state"> & {
    name: Omit<OptionType, "icon" | "tooltip">;
    state: Omit<OptionType, "icon" | "tooltip">;
  };
  loanNumber: string;
}

export const defaultValues = (
  isAdditionalInterestsEnabled?: boolean
): MortgageBillFormState => ({
  lender: {
    name: { value: "", label: "" },
    streetAddress: "",
    unit: "",
    city: "",
    state: { value: "", label: "" },
    zip: "",
    ...(isAdditionalInterestsEnabled ? { email: null } : {}),
  },
  loanNumber: "",
  customerESign: false,
  agentSignResponsibility: false,
});

export const createMortgageBillFormValidationSchema = (
  isD2C?: boolean,
  forceAgentSignResponsibility?: boolean | null,
  isAdditionalInterestsEnabled?: boolean
) =>
  object({
    lender: object({
      name: object({
        label: string().required(),
        value: string().required(),
      })
        .typeError("Lender name is required")
        .required("Lender name is required")
        .test({
          message: "Lender name is required",
          test: ({ value }) => !!value,
        }),
      streetAddress: nonEmptyString("Street address").required(
        "Street address is required"
      ),
      unit: unit().nullable(),
      city: nonEmptyString("City").required("City is required"),
      state: object({
        label: string().required(),
        value: string().required(),
      })
        .typeError("State is required")
        .required("State is required"),
      zip: string()
        .required("Zip code is required")
        .test(
          "len",
          "Zip Code must be 5 or 9 digits",
          (val) => val?.length === 5 || val?.length === 9
        ),
      ...(isAdditionalInterestsEnabled
        ? {
            email: emailOptional(),
          }
        : {}),
    }),
    loanNumber: nonEmptyString("Loan number")
      .required("Loan number is required")
      .max(25, "Loan number is too long"),
    customerESign: isD2C
      ? boolean().required().isTrue()
      : boolean().optional().nullable(),
    agentSignResponsibility:
      !isD2C && forceAgentSignResponsibility
        ? boolean().required().isTrue()
        : boolean().optional().nullable(),
  });
